import React from "react";
import { useTranslation } from "react-i18next";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import {
    Event,
    RecoveryChallengeResponse,
    RecoveryLinkResponse,
    RecoveryRestoreResponse,
} from "@uno/protocol";
import { AppContext } from "context";
import useStorePlayer from "store/useStorePlayer";
import { useShallow } from "zustand/react/shallow";

type Options = {
    isLogin: boolean;
    onLink: () => void;
    onRestore: (token: string) => void;
};

export default function usePasskey({ isLogin, onLink, onRestore }: Options) {
    const { t } = useTranslation();
    const { socket } = React.useContext(AppContext);
    const { playerId, username } = useStorePlayer(useShallow((state) => ({
        playerId: state.player?.id,
        username: state.player?.username,
    })));
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const challengeRef = React.useRef<RecoveryChallengeResponse | null>(null);

    const requestChallenge = React.useCallback((action: "link" | "restore"): Promise<RecoveryChallengeResponse | null> => {
        return new Promise((resolve) => {
            if(!socket) {
                resolve(null);
                return;
            }

            socket.emit(Event.RecoveryChallenge, { action }, resolve);
        });
    }, [socket]);

    React.useEffect(() => {
        const action = isLogin ? "restore" : "link";

        requestChallenge(action).then((challenge) => {
            challengeRef.current = challenge;
        });
    }, [isLogin, requestChallenge]);

    const connect = React.useCallback(async () => {
        if(!socket) {
            return;
        }

        setError(null);

        try {
            const challenge = challengeRef.current;

            if(!challenge?.passkey) {
                setError(t("signup.error"));
                return;
            }

            if(isLogin) {
                setLoading(true);
                const credential = await startAuthentication({
                    optionsJSON: {
                        challenge: challenge.passkey.challenge,
                        rpId: challenge.passkey.rpId,
                        userVerification: "preferred",
                        timeout: 120000,
                    },
                });

                challengeRef.current = null;

                const request = {
                    provider: "passkey" as const,
                    credential: {
                        id: credential.id,
                        rawId: credential.rawId,
                        type: credential.type,
                        response: {
                            clientDataJSON: credential.response.clientDataJSON,
                            authenticatorData: credential.response.authenticatorData,
                            signature: credential.response.signature,
                        },
                    },
                };

                socket.emit(Event.RecoveryRestore, request, (response: RecoveryRestoreResponse) => {
                    setLoading(false);

                    if(response.success && response.token) {
                        onRestore(response.token);
                        return;
                    }

                    setError(response.error === "no_backup"
                        ? t("signup.passkey_not_found")
                        : t("signup.error"));
                });
            } else {
                setLoading(true);
                const credential = await startRegistration({
                    optionsJSON: {
                        challenge: challenge.passkey.challenge,
                        rp: { id: challenge.passkey.rpId, name: challenge.passkey.rpName },
                        user: {
                            id: challenge.passkey.userHandle || playerId || crypto.randomUUID(),
                            name: username ? `${username}@${challenge.passkey.rpId}` : challenge.passkey.rpId,
                            displayName: username || challenge.passkey.rpId,
                        },
                        pubKeyCredParams: [
                            { alg: -7, type: "public-key" },
                            { alg: -257, type: "public-key" },
                        ],
                        authenticatorSelection: {
                            residentKey: "preferred",
                            userVerification: "preferred",
                        },
                        timeout: 120000,
                        attestation: "none",
                    },
                });

                challengeRef.current = null;

                const request = {
                    provider: "passkey" as const,
                    credential: {
                        id: credential.id,
                        rawId: credential.rawId,
                        type: credential.type,
                        response: {
                            clientDataJSON: credential.response.clientDataJSON,
                            attestationObject: credential.response.attestationObject,
                        },
                    },
                };

                socket.emit(Event.RecoveryLink, request, (response: RecoveryLinkResponse) => {
                    setLoading(false);

                    if(response.success) {
                        onLink();
                        return;
                    }

                    setError(response.error === "recovery_already_set"
                        ? t("signup.recovery_already_set")
                        : t("signup.error"));
                });
            }
        } catch{
            setLoading(false);
            setError(t("signup.passkey_cancelled"));
        }
    }, [socket, isLogin, onLink, onRestore, playerId, username, t, requestChallenge]);

    return { connect, loading, error };
}
